import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('ttouch_token'));
  const [loading, setLoading] = useState(true);
  const [requiresPasswordReset, setRequiresPasswordReset] = useState(false);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API_URL,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return instance;
  }, [token]);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await api().get('/auth/me');
          setUser(response.data);
          setRequiresPasswordReset(response.data.force_password_reset);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('ttouch_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token, api]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData, requires_password_reset } = response.data;
    
    localStorage.setItem('ttouch_token', newToken);
    setToken(newToken);
    setUser(userData);
    setRequiresPasswordReset(requires_password_reset);
    
    return { user: userData, requiresPasswordReset: requires_password_reset };
  };

  const logout = () => {
    localStorage.removeItem('ttouch_token');
    setToken(null);
    setUser(null);
    setRequiresPasswordReset(false);
  };

  const resetPassword = async (currentPassword, newPassword) => {
    await api().post('/auth/reset-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    setRequiresPasswordReset(false);
    
    // Re-fetch user to update force_password_reset flag
    const response = await api().get('/auth/me');
    setUser(response.data);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    api,
    requiresPasswordReset,
    resetPassword,
    isDirector: user?.role === 'director',
    isManager: user?.role === 'manager' || user?.role === 'director',
    isAdmin: user?.is_admin,
    isStaff: user?.role === 'staff',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
